import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SocketContext } from "../../../customComponents/SocketContext";
import { OtcTradeApi } from "./otcTradeApi";
import { isClosedRequest, receiveLine, receivesPaperDraft, requestWhen, stageLabel } from "./tradeStatus";
import "../OtcDesk/otc-desk.css";
import "./otc-trade.css";

function messageOf(res) {
  const text = (res && (res.message || res.code)) || "Request failed.";
  if (res && res.code === "INVENTORY_LOW") return "";
  if (/treasury does not have enough/i.test(String(text))) return "";
  return text;
}

function spotFree(row) {
  if (!row) return null;
  const free = Number(row.balance || 0) - Number(row.locked || 0);
  if (!Number.isFinite(free) || free <= 0) return "0";
  const trimmed = String(Math.round(free * 1e8) / 1e8);
  return trimmed;
}

function showTradeNotice(row) {
  if (!row) return;
  const reason = row.holdReason && (receivesPaperDraft(row) || !/collect your dd/i.test(row.holdReason)) ? ` ${row.holdReason}` : "";
  const text = `Request ${row.number} is ${stageLabel(row.status, row)}.${reason}`;
  toast.custom((item) => (
    <div className="otc-trade-notice" role="status">
      <span>{text}</span>
      <button type="button" aria-label="Close" onClick={() => toast.dismiss(item.id)}>×</button>
    </div>
  ), { position: "top-right", duration: 1000 });
}

function formatPrice(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || "—";
  return amount.toLocaleString("en-US", { maximumFractionDigits: 8 });
}

function trimAmount(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  return n.toFixed(8).replace(/\.?0+$/, "");
}

function sideRate(row, side) {
  if (!row) return "";
  if (side === "BUY") return row.buyPay || "";
  return row.sellGet || "";
}

function receivesDraft(row) {
  if (!row || row.category !== "fiat") return false;
  if (row.getKind === "fiat") return true;
  if (row.payKind === "fiat") return false;
  return true;
}

const SMALL_WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS_WORDS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const SCALE_WORDS = ["", "Thousand", "Million", "Billion", "Trillion"];

function underThousandWords(value) {
  if (value < 20) return SMALL_WORDS[value];
  if (value < 100) {
    const rest = value % 10;
    const tens = TENS_WORDS[Math.floor(value / 10)];
    return rest ? `${tens} ${SMALL_WORDS[rest]}` : tens;
  }
  const rest = value % 100;
  const head = `${SMALL_WORDS[Math.floor(value / 100)]} Hundred`;
  return rest ? `${head} and ${underThousandWords(rest)}` : head;
}

function integerWordsFromDigits(digits) {
  const text = String(digits || "").replace(/\D/g, "").replace(/^0+/, "") || "0";
  if (text === "0") return "Zero";
  if (text.length > 15) return "";
  const groups = [];
  for (let end = text.length; end > 0; end -= 3) {
    groups.push(Number(text.slice(Math.max(0, end - 3), end)));
  }
  const parts = [];
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    if (!groups[index]) continue;
    const words = underThousandWords(groups[index]);
    parts.push(SCALE_WORDS[index] ? `${words} ${SCALE_WORDS[index]}` : words);
  }
  const lowest = groups.findIndex((group) => group > 0);
  if (parts.length > 1 && lowest >= 0 && groups[lowest] < 100) {
    parts[parts.length - 1] = `and ${parts[parts.length - 1]}`;
  }
  return parts.join(" ");
}

function aedAmountInWords(amount) {
  const text = String(amount == null ? "" : amount).trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return "";
  let [dirhams, fraction = ""] = text.split(".");
  dirhams = dirhams.replace(/^0+/, "") || "0";
  let fils = Number((fraction + "00").slice(0, 2));
  if (fraction.length > 2 && fraction[2] >= "5") {
    fils += 1;
    if (fils >= 100) {
      fils = 0;
      const chars = dirhams.split("");
      let carry = 1;
      for (let index = chars.length - 1; index >= 0 && carry; index -= 1) {
        const next = Number(chars[index]) + carry;
        chars[index] = String(next % 10);
        carry = next >= 10 ? 1 : 0;
      }
      if (carry) chars.unshift("1");
      dirhams = chars.join("").replace(/^0+/, "") || "0";
    }
  }
  const dirhamValue = dirhams.replace(/^0+/, "") || "0";
  const parts = [];
  if (dirhamValue !== "0") parts.push(`${integerWordsFromDigits(dirhamValue)} ${dirhamValue === "1" ? "Dirham" : "Dirhams"}`);
  if (fils) parts.push(`${underThousandWords(fils)} ${fils === 1 ? "Fil" : "Fils"}`);
  if (!parts.length) return "";
  return parts.length === 2 ? `${parts[0]} and ${parts[1]} Only` : `${parts[0]} Only`;
}

function estimatedGet(pair, side, payAmount) {
  const pay = Number(payAmount);
  const rate = Number(sideRate(pair, side));
  if (!pair || !(pay > 0) || !(rate > 0)) return "";
  if (side === "BUY") return trimAmount(pay / rate);
  return trimAmount(pay * rate);
}

function OtcFlowSteps({ active, compact = false }) {
  const items = [
    { n: 1, label: "Pair", hint: "Pick market" },
    { n: 2, label: "Quote", hint: "Enter amount" },
    { n: 3, label: "Confirm", hint: "Lock funds" },
    { n: 4, label: "Receive", hint: "Settle / DD" },
  ];
  const progress = Math.max(0, Math.min(1, (Number(active) - 1) / (items.length - 1)));
  return (
    <div className={`otc-flow-steps${compact ? " is-compact" : ""}`} aria-label="OTC trade steps">
      <div className="otc-flow-steps__track" aria-hidden="true">
        <span className="otc-flow-steps__track-fill" style={{ width: `${progress * 100}%` }} />
      </div>
      <ol className="otc-flow-steps__list">
        {items.map((item) => (
          <li
            key={item.n}
            className={active === item.n ? "is-on" : active > item.n ? "is-done" : ""}
            aria-current={active === item.n ? "step" : undefined}
          >
            <span className="otc-flow-steps__num">{active > item.n ? <i className="ri-check-line" aria-hidden="true" /> : item.n}</span>
            <span className="otc-flow-steps__copy">
              <strong>{item.label}</strong>
              {!compact ? <em>{item.hint}</em> : null}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function OtcTradeDesk() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openQuoteFromLink = searchParams.get("open") === "quote";
  const [category, setCategory] = useState("crypto");
  const [pairs, setPairs] = useState([]);
  const [pairId, setPairId] = useState("");
  const [side, setSide] = useState("SELL");
  const [payAmount, setPayAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(openQuoteFromLink);
  const [popup, setPopup] = useState(false);
  const [confirmKey, setConfirmKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState(null);
  const [mustChoose, setMustChoose] = useState(false);
  const [detailPath, setDetailPath] = useState("");
  const [details, setDetails] = useState({
    destination: "spot",
    reference: "",
    purpose: "",
    payeeName: "",
    payeeConfirm: "",
    nameConfirmed: false,
  });
  const [detailError, setDetailError] = useState("");
  const [requests, setRequests] = useState([]);
  const [pairQuery, setPairQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [historyView, setHistoryView] = useState(null);
  const { socket } = useContext(SocketContext) || {};
  const loadSeq = useRef(0);

  const pair = pairs.find((row) => row.id === pairId) || null;

  const loadPairs = useCallback(() => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    return Promise.all([OtcTradeApi.pairs("crypto"), OtcTradeApi.pairs("fiat")]).then(([cryptoRes, fiatRes]) => {
      if (seq !== loadSeq.current) return;
      const failed = [cryptoRes, fiatRes].find((res) => !res || res.success === false);
      if (failed) {
        setError(messageOf(failed));
        setPairs([]);
        return;
      }
      const crypto = Array.isArray(cryptoRes.data) ? cryptoRes.data : [];
      const fiat = Array.isArray(fiatRes.data) ? fiatRes.data : [];
      const rows = [...crypto, ...fiat];
      setPairs(rows);
      setPairId((current) => (rows.some((row) => row.id === current) ? current : (rows[0] ? rows[0].id : "")));
    });
  }, []);

  useEffect(() => {
    loadPairs();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadPairs();
    }, 3000);
    const onVisible = () => {
      if (document.visibilityState === "visible") loadPairs();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadPairs]);

  useEffect(() => {
    if (!openQuoteFromLink) return;
    setQuoteOpen(true);
    if (searchParams.has("open")) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [openQuoteFromLink, searchParams, setSearchParams]);

  useEffect(() => {
    if (!quoteOpen || pairId || !pairs.length) return;
    const first = pairs.find((row) => row.category === category) || pairs[0];
    if (first) {
      setCategory(first.category === "fiat" ? "fiat" : "crypto");
      setPairId(first.id);
    }
  }, [quoteOpen, pairId, pairs, category]);

  useEffect(() => {
    if (!socket) return undefined;
    const refresh = () => { loadPairs(); };
    socket.on("otc-trade:pairs", refresh);
    return () => socket.off("otc-trade:pairs", refresh);
  }, [socket, loadPairs]);

  const categoryPairs = pairs.filter((row) => row.category === category);
  const activeRequests = requests.filter((row) => !isClosedRequest(row.status));
  const query = pairQuery.trim().toLowerCase();
  const searchPairs = categoryPairs.filter((row) => {
    if (!query) return true;
    return `${row.symbol || ""} ${row.base || ""} ${row.quote || ""}`.toLowerCase().includes(query);
  });

  useEffect(() => {
    if (!categoryPairs.length) return;
    if (!categoryPairs.some((row) => row.id === pairId)) setPairId(categoryPairs[0].id);
  }, [category, categoryPairs, pairId]);

  useEffect(() => {
    if (!pair) return undefined;
    const asset = side === "BUY" ? pair.quote : pair.base;
    let stop = false;
    OtcTradeApi.balance(asset).then((res) => {
      if (!stop && res && res.success !== false) setBalance(res.data);
    });
    return () => {
      stop = true;
    };
  }, [pair, side]);

  useEffect(() => {
    if (!pairId || !(Number(payAmount) > 0)) {
      setQuote(null);
      return undefined;
    }
    let stop = false;
    const timer = setTimeout(() => {
      OtcTradeApi.quote({ pairId, side, payAmount }).then((res) => {
        if (stop) return;
        if (!res || res.success === false) {
          setQuote(null);
          setError(messageOf(res));
          return;
        }
        setError("");
        setQuote(res.data);
      }).catch(() => {
        if (stop) return;
        setQuote(null);
        setError("The price could not be loaded.");
      });
    }, 400);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [pairId, side, payAmount]);

  const loadRequests = useCallback(() => {
    OtcTradeApi.requests().then((res) => {
      if (res && res.success !== false) setRequests(Array.isArray(res.data) ? res.data : []);
    });
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const payAsset = quote ? quote.payAsset : pair ? (side === "BUY" ? pair.quote : pair.base) : "";
  const getAsset = quote ? quote.getAsset : pair ? (side === "BUY" ? pair.base : pair.quote) : "";
  const available = spotFree(balance);
  const liveGet = estimatedGet(pair, side, payAmount);
  const shownGet = quote && quote.side === side && Number(quote.payAmount) === Number(payAmount)
    ? quote.getAmount
    : liveGet;
  const shortBalance = available != null && quote && Number(available) < Number(quote.payAmount);
  const shownError = /treasury does not have enough/i.test(String(error || "")) ? "" : error;
  const blocked = !quote || shortBalance;

  const openPair = (row, { keepPopup = true } = {}) => {
    setCategory(row.category === "fiat" ? "fiat" : "crypto");
    setPairId(row.id);
    setSide("SELL");
    setPayAmount("");
    setQuote(null);
    setError("");
    setPairQuery("");
    setSearchOpen(false);
    if (keepPopup) setQuoteOpen(true);
  };

  // 1 Pair → 2 Quote → 3 Confirm → 4 Settle/DD
  const flowStep = choice ? 4 : popup ? 3 : quoteOpen ? 2 : 1;

  const openConfirm = () => {
    if (blocked) return;
    setConfirmKey(`otc-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    setPopup(true);
  };

  const submit = async () => {
    if (!quote || !confirmKey || submitting) return;
    setSubmitting(true);
    const fresh = await OtcTradeApi.quote({ pairId, side, payAmount: quote.payAmount });
    if (!fresh || fresh.success === false || !fresh.data || !fresh.data.quoteId) {
      setSubmitting(false);
      setError(messageOf(fresh));
      setPopup(false);
      return;
    }
    setQuote(fresh.data);
    const res = await OtcTradeApi.confirm(fresh.data.quoteId, confirmKey);
    setSubmitting(false);
    if (!res || res.success === false) {
      setSubmitting(false);
      const text = messageOf(res);
      if (text) setError(text);
      setPopup(false);
      return;
    }
    setChoice(res.data);
    setMustChoose(true);
    setDetailPath(res.data && res.data.category === "fiat" ? "dd" : "settlement");
    setDetails({ destination: "spot", reference: "", purpose: "", payeeName: "", payeeConfirm: "", nameConfirmed: false });
    setDetailError("");
    setPopup(false);
    setPayAmount("");
    setQuote(null);
  };

  const openDetails = (kind, row) => {
    if (row) setChoice(row);
    setMustChoose(false);
    setDetailPath(kind);
    setDetails({ destination: "spot", reference: "", purpose: "", payeeName: "", payeeConfirm: "", nameConfirmed: false });
    setDetailError("");
  };

  const closeChoice = () => {
    if (mustChoose) return;
    setChoice(null);
    setDetailPath("");
    setDetailError("");
  };

  const pickPath = async (kind) => {
    if (!choice || submitting) return;
    const paperDd = kind === "dd" && receivesDraft(choice);
    const fiatToCrypto = kind === "dd" && !receivesDraft(choice);
    const offline = fiatToCrypto && details.destination === "wallet";
    const useSettlement = kind === "settlement" || (fiatToCrypto && !offline);
    const destination = paperDd || useSettlement
      ? "spot"
      : offline
        ? "wallet"
        : (details.destination || "spot");
    const reference = String(details.reference || "").trim();
    const purpose = String(details.purpose || "").trim();
    const payeeName = String(details.payeeName || "").trim();
    const payeeConfirm = String(details.payeeConfirm || "").trim();
    if (paperDd) {
      if (String(choice.getAsset || "").toUpperCase() !== "AED") {
        setDetailError("A demand draft can only be issued in AED.");
        return;
      }
      const problems = [];
      if (!payeeName) problems.push("Enter the name to print on the demand draft.");
      else if (payeeName !== payeeConfirm) problems.push("Payee name and confirm payee name must match.");
      if (!details.nameConfirmed) problems.push("Confirm that the name on the demand draft is correct.");
      if (!purpose) problems.push("Enter the purpose of this payment.");
      if (problems.length) {
        setDetailError(problems.join(" "));
        return;
      }
    } else if (fiatToCrypto) {
      if (!purpose) {
        setDetailError("Enter the purpose of this trade.");
        return;
      }
      if (offline && !reference) {
        setDetailError("Enter the wallet address that should receive the crypto.");
        return;
      }
    } else if (destination !== "spot" && !reference) {
      setDetailError("Enter the account or wallet that should receive the amount.");
      return;
    }
    const id = choice._id || choice.id;
    const body = {
      destination,
      reference: destination === "spot" ? "" : reference,
      purpose,
      payeeName,
      payeeConfirm,
      nameConfirmed: Boolean(details.nameConfirmed),
    };
    setSubmitting(true);
    const res = useSettlement
      ? await OtcTradeApi.chooseSettlement(id, body)
      : await OtcTradeApi.chooseDd(id, body);
    setSubmitting(false);
    if (!res || res.success === false) {
      setDetailError(messageOf(res));
      return;
    }
    setMustChoose(false);
    setChoice(null);
    setDetailPath("");
    setDetailError("");
    setPopup(false);
    setHistoryView(null);
    setPayAmount("");
    setQuote(null);
    setError("");
    showTradeNotice(res.data);
    loadRequests();
    setQuoteOpen(false);
    navigate("/user_profile/orders/otc_orders");
  };

  const closeQuotePopup = () => {
    setQuoteOpen(false);
    setPayAmount("");
    setQuote(null);
    setError("");
    setPopup(false);
  };

  return (
    <div className="dashboard_right otc-desk-page otc-trade-desk otc-trade-shell otc-trade-shell--orders">
      <Helmet>
        <title>OTC Trade Desk | AGCX</title>
      </Helmet>
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page otc-desk-flow" aria-labelledby="otc-desk-title">
            <header className="otc-desk-flow__hero">
              <p className="otc-desk-flow__kicker">OTC trading</p>
              <h1 id="otc-desk-title" className="so-page__title">Trade Desk</h1>
              <p className="otc-desk-flow__lead">Follow the OTC steps: choose a pair, request a quote, confirm the lock, then settle or create a demand draft.</p>
            </header>

            <OtcFlowSteps active={flowStep} />

            <section className="otc-desk-flow__panel" aria-labelledby="otc-pair-step-title">
              <div className="otc-desk-flow__panel-head">
                <div>
                  <p className="otc-desk-flow__eyebrow">Step 1 of 4</p>
                  <h2 id="otc-pair-step-title" className="otc-desk-flow__panel-title">Select a trading pair</h2>
                </div>
                <div className="otc-exchange-tabs otc-trade-kind-tabs" role="tablist" aria-label="Pair type">
                  <button type="button" role="tab" aria-selected={category === "crypto"} className={category === "crypto" ? "is-on" : ""} onClick={() => { setCategory("crypto"); setPairQuery(""); }}>Crypto</button>
                  <button type="button" role="tab" aria-selected={category === "fiat"} className={category === "fiat" ? "is-on" : ""} onClick={() => { setCategory("fiat"); setPairQuery(""); }}>Fiat</button>
                </div>
              </div>

              <label className="otc-desk-flow__search">
                <i className="ri-search-line" aria-hidden="true" />
                <input
                  value={pairQuery}
                  onChange={(event) => setPairQuery(event.target.value)}
                  placeholder={category === "fiat" ? "Search fiat pair" : "Search crypto pair"}
                  aria-label="Search pair"
                  autoComplete="off"
                />
              </label>

              {!searchPairs.length ? (
                <div className="otc-desk-flow__empty" role="status">
                  <img src="/images/no-data.svg" alt="" width={96} height={116} />
                  <p>No pairs match this filter.</p>
                </div>
              ) : (
                <div className="otc-desk-flow__grid" role="list">
                  {searchPairs.map((row) => (
                    <button type="button" key={row.id} role="listitem" className="otc-desk-pair" onClick={() => openPair(row)}>
                      <span className="otc-desk-pair__glow" aria-hidden="true" />
                      <span className="otc-desk-pair__symbol">{row.symbol}</span>
                      <span className="otc-desk-pair__price">
                        <span>Indicative</span>
                        <strong>{formatPrice(sideRate(row, "SELL") || row.price)}</strong>
                      </span>
                      <span className="otc-desk-pair__cta">Request quote <i className="ri-arrow-right-s-line" aria-hidden="true" /></span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {activeRequests.some((row) => row.status === "AWAITING_CHOICE") ? (
              <div className="otc-open-requests otc-desk-flow__pending">
                <h2 className="otc-desk-flow__panel-title">Needs your next step</h2>
                {activeRequests.filter((row) => row.status === "AWAITING_CHOICE").map((row) => (
                  <button type="button" key={row._id || row.id || row.number} onClick={() => openDetails(row.category === "fiat" ? "dd" : "settlement", row)}>
                    {row.number} · {row.category === "fiat" ? (receivesDraft(row) ? "Demand draft" : "Receive crypto") : "Settlement"}
                  </button>
                ))}
              </div>
            ) : null}
          </main>
        </div>
      </div>
      {quoteOpen && pair ? createPortal(
        <div className="otc-trade-overlay otc-trade-overlay--aside-safe otc-quote-modal-wrap otc-trade-desk" onClick={closeQuotePopup}>
          <div
            className="otc-card otc-rfq-card otc-trade-home-card otc-quote-modal otc-quote-modal--themed"
            role="dialog"
            aria-modal="true"
            aria-labelledby="otc-request-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="otc-quote-modal__top">
              <button type="button" className="otc-quote-back" onClick={closeQuotePopup}>
                <i className="ri-arrow-left-s-line" aria-hidden="true" /> Back
              </button>
              <button type="button" className="otc-trade-close" aria-label="Close" onClick={closeQuotePopup}>×</button>
            </div>
            <p className="otc-quote-modal__eyebrow">Step 2 of 4</p>
            <h3 id="otc-request-title" className="otc-card-title">Request Quote</h3>
            <p className="otc-quote-modal__hint">Pick a pair, enter how much you pay, then continue to lock.</p>
            <OtcFlowSteps active={flowStep} compact />
            <div className="otc-exchange-tabs otc-trade-kind-tabs" role="tablist" aria-label="Pair type">
              <button type="button" role="tab" aria-selected={category === "crypto"} className={category === "crypto" ? "is-on" : ""} onClick={() => { setCategory("crypto"); setPairQuery(""); setSearchOpen(false); }}>Crypto</button>
              <button type="button" role="tab" aria-selected={category === "fiat"} className={category === "fiat" ? "is-on" : ""} onClick={() => { setCategory("fiat"); setPairQuery(""); setSearchOpen(false); }}>Fiat</button>
            </div>
            <div className="otc-pair-search">
              <div className="quote-input-card">
                <i className="ri-search-line" aria-hidden="true" />
                <input
                  className="quote-field-input"
                  value={pairQuery}
                  onChange={(event) => { setPairQuery(event.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => { window.setTimeout(() => setSearchOpen(false), 160); }}
                  placeholder={category === "fiat" ? "Search fiat pair" : "Search crypto pair"}
                  aria-label="Search pair"
                  autoComplete="off"
                />
              </div>
              {searchOpen ? (
                <ul className="otc-pair-search-list">
                  {!searchPairs.length ? <li className="otc-pair-search-empty">No pair found</li> : searchPairs.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className={row.id === pairId ? "is-on" : ""}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openPair(row)}
                      >
                        <span>{row.symbol}</span>
                        <span>{formatPrice(row.price)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="otc-active-rail otc-active-rail--quote" role="listbox" aria-label="Pairs">
              {categoryPairs.slice(0, 5).map((row) => (
                <button
                  type="button"
                  key={row.id}
                  role="option"
                  aria-selected={row.id === pairId}
                  className={row.id === pairId ? "is-on" : ""}
                  onClick={() => openPair(row)}
                >
                  <span>{row.symbol}</span>
                  <span>{formatPrice(sideRate(row, side) || row.price)}</span>
                </button>
              ))}
            </div>
            <div className="otc-quote-modal__pair">
              <div className="otc-quote-modal__pair-main">
                <span className="otc-quote-modal__pair-label">Selected pair</span>
                <strong>{pair.symbol}</strong>
              </div>
              <div className="otc-quote-modal__pair-price">
                <span>Indicative</span>
                <strong>{formatPrice(sideRate(pair, side) || pair.price)}</strong>
              </div>
            </div>
            <div className="quote-field-group">
              <div className="quote-field-header">
                <label className="quote-field-label">You pay</label>
                <div className="quote-avail-wrap">
                  <span className="quote-avail-text otc-balance-tip">
                    Available: {available != null ? available : "—"} {payAsset}
                    {balance && Number(balance.locked) > 0 ? ` · Locked: ${balance.locked}` : ""}
                    <span className="otc-balance-tip__icon">
                      <i className="ri-information-line" aria-hidden="true" />
                      <span className="otc-balance-tip__text" role="tooltip">This is the free balance in your spot wallet.</span>
                    </span>
                  </span>
                  <button type="button" className="otc-inline-max-btn" onClick={() => { if (available != null) setPayAmount(available); }}>Max</button>
                </div>
              </div>
              <div className="quote-input-card quote-amount-card">
                <input
                  className="quote-field-input quote-amount-input"
                  value={payAmount}
                  onChange={(event) => setPayAmount(event.target.value)}
                  inputMode="decimal"
                  placeholder="0.00"
                  autoComplete="off"
                />
                <div className="quote-amount-side">
                  <span className="otc-coin-label">{payAsset || "—"}</span>
                </div>
              </div>
            </div>
            <div className="otc-trade-swap-wrap">
              <button
                type="button"
                className="otc-trade-swap"
                title="Swap you pay and you get"
                onClick={() => {
                  setError("");
                  setPayAmount("");
                  setQuote(null);
                  setSide((current) => (current === "BUY" ? "SELL" : "BUY"));
                }}
              >
                <i className="ri-arrow-up-down-line" aria-hidden="true" />
              </button>
            </div>
            <div className="quote-field-group">
              <div className="quote-field-header">
                <label className="quote-field-label">You get</label>
              </div>
              <div className="quote-input-card quote-amount-card">
                <input className="quote-field-input quote-amount-input" readOnly value={shownGet} placeholder="0.00" />
                <div className="quote-amount-side">
                  <span className="otc-coin-label">{getAsset || "—"}</span>
                </div>
              </div>
            </div>
            {shortBalance ? <p className="quote-field-error">Insufficient {payAsset} spot balance.</p> : null}
            {shownError ? <p className="quote-field-error">{shownError}</p> : null}
            <div className="otc-quote-modal__actions">
              <button type="button" className="btn-request-quote" disabled={blocked} onClick={openConfirm}>Continue to confirm</button>
              <button type="button" className="otc-trades-link" onClick={() => navigate("/user_profile/orders/otc_orders")}>My trades</button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {historyView ? createPortal(
        <div className="otc-trade-overlay otc-trade-overlay--aside-safe">
          <div className="otc-trade-popup" role="dialog" aria-modal="true" aria-labelledby="otc-history-view-title">
            <button type="button" className="otc-trade-close" aria-label="Close" onClick={() => setHistoryView(null)}>×</button>
            <h2 id="otc-history-view-title">{historyView.number}</h2>
            <p className="otc-trade-lead">{requestWhen(historyView.createdAt || historyView.updatedAt)}</p>
            <dl>
              <dt>Pair</dt><dd>{historyView.pairSymbol}</dd>
              <dt>Type</dt><dd>{historyView.category === "fiat" ? "Fiat spot" : "Crypto"}</dd>
              <dt>You pay</dt><dd>{historyView.payAmount} {historyView.payAsset}</dd>
              <dt>You get</dt><dd>{historyView.getAmount} {historyView.getAsset}</dd>
              <dt>Status</dt><dd>{stageLabel(historyView.status, historyView)}</dd>
              <dt>Receive</dt><dd>{receiveLine(historyView)}</dd>
              <dt>Purpose</dt><dd>{(historyView.dd && historyView.dd.purpose) || (historyView.clientDetails && historyView.clientDetails.purpose) || "—"}</dd>
              {historyView.dd && historyView.dd.direction === "crypto_to_fiat" ? (
                <>
                  <dt>Currency</dt><dd>{historyView.dd.currency || "AED"}</dd>
                  <dt>Demand draft amount</dt><dd>{historyView.dd.amount || historyView.getAmount} {historyView.dd.currency || historyView.getAsset}</dd>
                  <dt>Amount in words</dt><dd>{historyView.dd.amountInWords || "—"}</dd>
                  <dt>Collection</dt><dd>Office Collection</dd>
                </>
              ) : null}
              {historyView.status === "REJECTED" ? <><dt>Reason</dt><dd>{historyView.rejectReason || "—"}</dd></> : null}
            </dl>
          </div>
        </div>,
        document.body
      ) : null}
      {popup && quote ? createPortal(
        <div className="otc-trade-overlay otc-trade-overlay--aside-safe otc-trade-desk">
          <div className="otc-trade-popup otc-card otc-rfq-card otc-trade-home-card otc-quote-modal" role="dialog" aria-modal="true" aria-labelledby="otc-confirm-title">
            <h3 id="otc-confirm-title" className="otc-card-title">Step 3 · Confirm OTC trade</h3>
            <OtcFlowSteps active={flowStep} compact />
            <p className="otc-trade-lead">Nothing is locked until you confirm.</p>
            <div className="quote-pair-price-row quote-input-card">
              <span className="quote-live-last">{(quote.pair && quote.pair.symbol) || "—"}</span>
              <div className="quote-live-price">
                <span className="quote-live-last">{formatPrice(sideRate(quote.pair, quote.side) || (quote.pair && quote.pair.price) || quote.price)}</span>
              </div>
            </div>
            <div className="quote-field-group">
              <div className="quote-field-header">
                <label className="quote-field-label">You pay</label>
                <span className="quote-avail-text">Available: {available != null ? available : "—"} {quote.payAsset}</span>
              </div>
              <div className="quote-input-card quote-amount-card">
                <input className="quote-field-input quote-amount-input" readOnly value={quote.payAmount} />
                <div className="quote-amount-side">
                  <span className="otc-coin-label">{quote.payAsset}</span>
                </div>
              </div>
            </div>
            <div className="quote-field-group">
              <div className="quote-field-header">
                <label className="quote-field-label">You get</label>
              </div>
              <div className="quote-input-card quote-amount-card">
                <input className="quote-field-input quote-amount-input" readOnly value={quote.getAmount} />
                <div className="quote-amount-side">
                  <span className="otc-coin-label">{quote.getAsset}</span>
                </div>
              </div>
            </div>
            <p className="otc-trade-note">When you confirm, {quote.payAmount} {quote.payAsset} is locked in your spot wallet. You cannot use this locked amount anywhere else. Next step: {quote.pair && quote.pair.category === "fiat" ? "create a demand draft" : "send for settlement"}.</p>
            <div className="otc-trade-footer">
              <button type="button" className="otc-trade-back" onClick={() => setPopup(false)}>Back</button>
              <button type="button" className="otc-trade-confirm" disabled={submitting} onClick={submit}>
                {submitting ? "Confirming…" : "Confirm trade"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {choice ? createPortal(
        <div className="otc-trade-overlay otc-trade-overlay--aside-safe">
          <div className="otc-trade-popup otc-quote-modal" role="dialog" aria-modal="true" aria-labelledby="otc-choice-title">
            {mustChoose ? null : <button type="button" className="otc-trade-close" aria-label="Close" onClick={closeChoice}>×</button>}
            <OtcFlowSteps active={flowStep} compact />
            {detailPath === "dd" && receivesDraft(choice) ? (
              <>
                <h2 id="otc-choice-title">Step 4 · Demand draft</h2>
                <p>Request {choice.number} stays locked. The name is printed exactly as you type it. You collect the draft from the office.</p>
                <label className="otc-trade-detail">
                  <span>Payee name</span>
                  <input value={details.payeeName} onChange={(event) => setDetails((prev) => ({ ...prev, payeeName: event.target.value }))} />
                </label>
                <label className="otc-trade-detail">
                  <span>Confirm payee name</span>
                  <input value={details.payeeConfirm} onChange={(event) => setDetails((prev) => ({ ...prev, payeeConfirm: event.target.value }))} />
                </label>
                <label className="otc-trade-detail">
                  <span>Purpose of payment</span>
                  <input value={details.purpose} onChange={(event) => setDetails((prev) => ({ ...prev, purpose: event.target.value }))} placeholder="Why this payment" />
                </label>
                <label className="otc-trade-detail">
                  <span>Currency</span>
                  <input readOnly value={choice.getAsset || "AED"} />
                </label>
                <label className="otc-trade-detail">
                  <span>Demand draft amount</span>
                  <input readOnly value={choice.getAmount || ""} />
                </label>
                <label className="otc-trade-detail">
                  <span>Amount in words</span>
                  <input readOnly value={String(choice.getAsset || "").toUpperCase() === "AED" ? aedAmountInWords(choice.getAmount) : ""} />
                </label>
                <label className="otc-trade-detail">
                  <span>Trading pair</span>
                  <input readOnly value={choice.pairSymbol || ""} />
                </label>
                <label className="otc-trade-detail">
                  <span>Collection method</span>
                  <input readOnly value="Office Collection" />
                </label>
                <label className="otc-trade-detail">
                  <span>
                    <input type="checkbox" checked={details.nameConfirmed} onChange={(event) => setDetails((prev) => ({ ...prev, nameConfirmed: event.target.checked }))} />
                    {" "}I have checked and verified the name to be printed on the cheque.
                  </span>
                </label>
              </>
            ) : detailPath === "dd" ? (
              <>
                <h2 id="otc-choice-title">Step 4 · Choose how to receive crypto</h2>
                <p>Request {choice.number} stays locked. Spot goes to settlement; offline uses a separate send workflow.</p>
                <fieldset className="otc-trade-detail otc-receive-choice">
                  <legend>Receive crypto to</legend>
                  <label className="otc-receive-choice__option">
                    <input
                      type="radio"
                      name="otc-receive"
                      checked={details.destination !== "wallet"}
                      onChange={() => setDetails((prev) => ({ ...prev, destination: "spot", reference: "" }))}
                    />
                    <span>Spot wallet (settlement)</span>
                  </label>
                  <label className="otc-receive-choice__option">
                    <input
                      type="radio"
                      name="otc-receive"
                      checked={details.destination === "wallet"}
                      onChange={() => setDetails((prev) => ({ ...prev, destination: "wallet" }))}
                    />
                    <span>Offline / external wallet</span>
                  </label>
                </fieldset>
                {details.destination === "wallet" ? (
                  <label className="otc-trade-detail">
                    <span>Wallet address</span>
                    <input
                      value={details.reference}
                      onChange={(event) => setDetails((prev) => ({ ...prev, reference: event.target.value }))}
                      placeholder="Address that should receive the crypto"
                      autoComplete="off"
                    />
                  </label>
                ) : null}
                <label className="otc-trade-detail">
                  <span>Purpose</span>
                  <input value={details.purpose} onChange={(event) => setDetails((prev) => ({ ...prev, purpose: event.target.value }))} placeholder="Why this trade" />
                </label>
              </>
            ) : (
              <>
                <h2 id="otc-choice-title">Step 4 · Settlement</h2>
                <p>Request {choice.number} stays locked. The amount you get goes to your spot wallet.</p>
                <label className="otc-trade-detail">
                  <span>Receive to</span>
                  <input readOnly value="Keep in my spot wallet" />
                </label>
                <label className="otc-trade-detail">
                  <span>Purpose (optional)</span>
                  <input value={details.purpose} onChange={(event) => setDetails((prev) => ({ ...prev, purpose: event.target.value }))} placeholder="Why this trade" />
                </label>
              </>
            )}
            {detailError ? <p className="otc-trade-error">{detailError}</p> : null}
            <div className={mustChoose ? "otc-trade-footer otc-trade-footer-one" : "otc-trade-footer"}>
              {mustChoose ? null : <button type="button" className="otc-trade-back" disabled={submitting} onClick={closeChoice}>Back</button>}
              <button type="button" className="otc-trade-confirm" disabled={submitting} onClick={() => pickPath(detailPath || (choice.category === "fiat" ? "dd" : "settlement"))}>
                {submitting
                  ? "Sending…"
                  : detailPath === "dd" && receivesDraft(choice)
                    ? "Create DD"
                    : detailPath === "dd" && !receivesDraft(choice)
                      ? (details.destination === "wallet" ? "Confirm offline receive" : "Send for settlement")
                      : "Send for settlement"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}
